import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Trash2,
  RotateCcw,
  Search,
  Inbox,
  AlertTriangle,
} from "lucide-react";
import api, { adminReq } from "../../lib/api";
import { toast } from "sonner";
import useSelection from "../../hooks/useSelection";
import SelectionBar from "../../components/SelectionBar";
import CheckboxCell from "../../components/CheckboxCell";

const KIND_META = {
  applications: {
    label: "Student Applications",
    accent: "#1E3A8A",
    title: (r) => r.full_name,
    sub: (r) => r.email + " · " + (r.program || "No program"),
  },
  domestic: {
    label: "Domestic Applications",
    accent: "#C49A3A",
    title: (r) =>
      `${r.first_name || ""} ${r.last_name || ""}`.trim() ||
      r.email,
    sub: (r) => `${r.email} · ${r.program_title || "—"}`,
  },
  recruiters: {
    label: "Recruiters",
    accent: "#059669",
    title: (r) => r.full_name,
    sub: (r) => `${r.email} · ${r.company_name}`,
  },
  "student-aid": {
    label: "Student Aid Records",
    accent: "#7C3AED",
    title: (r) =>
      `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.student_id,
    sub: (r) => `${r.student_id || "—"} · ${r.program || "—"}`,
  },
};

function Section({ kind, items, refresh }) {
  const meta = KIND_META[kind];
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!q) return items;
    return items.filter((r) =>
      JSON.stringify(r).toLowerCase().includes(q.toLowerCase())
    );
  }, [items, q]);
  const sel = useSelection(filtered);
  const [confirm, setConfirm] = useState(null); // { action, count }

  const restore = async (ids) => {
    try {
      await api.post(
        `/admin/${kind}/bulk-restore`,
        { ids },
        adminReq()
      );
      toast.success(`${ids.length} restored`);
      sel.clear();
      refresh();
    } catch (e) {
      toast.error("Failed to restore");
    }
  };

  const purge = async (ids) => {
    try {
      await api.post(
        `/admin/${kind}/bulk-purge`,
        { ids },
        adminReq()
      );
      toast.success(`${ids.length} permanently deleted`);
      sel.clear();
      refresh();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden"
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{
              background: `${meta.accent}15`,
              color: meta.accent,
            }}
          >
            <Trash2 className="w-4 h-4" />
          </div>
          <div>
            <h3 className="font-display text-base font-semibold text-slate-900">
              {meta.label}
            </h3>
            <p className="text-xs text-slate-500">
              {items.length} item{items.length !== 1 ? "s" : ""} in trash
            </p>
          </div>
        </div>
        {items.length > 0 && (
          <div className="flex-1 sm:flex-initial flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-1.5 sm:w-72">
            <Search className="w-3.5 h-3.5 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Search ${meta.label.toLowerCase()}...`}
              className="bg-transparent outline-none text-sm flex-1"
            />
          </div>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="p-12 text-center text-slate-400 text-sm flex flex-col items-center gap-2">
          <Inbox className="w-8 h-8 text-slate-300" />
          {q ? "No matches" : "Nothing here"}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/80 text-left text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-2.5 w-10">
                  <CheckboxCell
                    checked={sel.allSelected}
                    indeterminate={sel.someSelected}
                    onChange={sel.toggleAll}
                    ariaLabel="Select all"
                  />
                </th>
                <th className="px-4 py-2.5">Item</th>
                <th className="px-4 py-2.5">Deleted</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const checked = sel.isSelected(r.id);
                return (
                  <tr
                    key={r.id}
                    onClick={() => sel.toggle(r.id)}
                    className={`border-t border-slate-100 cursor-pointer ${
                      checked
                        ? "bg-amber-50/60"
                        : "hover:bg-slate-50/70"
                    }`}
                  >
                    <td className="px-4 py-3">
                      <CheckboxCell
                        checked={checked}
                        onChange={() => sel.toggle(r.id)}
                        ariaLabel="Select item"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">
                        {meta.title(r)}
                      </div>
                      <div className="text-xs text-slate-500 truncate max-w-md">
                        {meta.sub(r)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {r.deleted_at
                        ? new Date(r.deleted_at).toLocaleString()
                        : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            restore([r.id]);
                          }}
                          className="inline-flex items-center gap-1 text-emerald-600 hover:underline text-xs font-semibold"
                        >
                          <RotateCcw className="w-3 h-3" /> Restore
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirm({ action: "purge", ids: [r.id] });
                          }}
                          className="inline-flex items-center gap-1 text-red-600 hover:underline text-xs font-semibold ml-2"
                        >
                          <Trash2 className="w-3 h-3" /> Delete forever
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <SelectionBar
        count={sel.count}
        onClear={sel.clear}
        noun="item"
        actions={[
          {
            label: "Restore",
            icon: RotateCcw,
            variant: "success",
            onClick: () => restore(sel.selectedIds),
          },
          {
            label: "Delete forever",
            icon: Trash2,
            variant: "danger",
            onClick: () =>
              setConfirm({ action: "purge", ids: sel.selectedIds }),
          },
        ]}
      />

      {confirm && confirm.action === "purge" && (
        <ConfirmModal
          title="Permanently delete?"
          message={`This will permanently delete ${confirm.ids.length} item${
            confirm.ids.length !== 1 ? "s" : ""
          }. This action cannot be undone.`}
          confirmLabel="Yes, delete forever"
          danger
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            purge(confirm.ids);
            setConfirm(null);
          }}
        />
      )}
    </motion.div>
  );
}

export function ConfirmModal({
  title,
  message,
  confirmLabel,
  danger,
  onCancel,
  onConfirm,
}) {
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6"
      >
        <div className="flex items-start gap-3">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
              danger ? "bg-red-50" : "bg-amber-50"
            }`}
          >
            <AlertTriangle
              className={`w-5 h-5 ${
                danger ? "text-red-600" : "text-amber-600"
              }`}
            />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-lg font-semibold text-slate-900">
              {title}
            </h3>
            <p className="mt-1 text-sm text-slate-600">{message}</p>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm font-semibold text-white rounded-md ${
              danger
                ? "bg-red-600 hover:bg-red-700"
                : "bg-cct-blue hover:bg-[#1a3380]"
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export default function AdminTrash() {
  const [trash, setTrash] = useState({
    applications: [],
    domestic: [],
    recruiters: [],
    "student-aid": [],
  });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/trash", adminReq());
      setTrash(data);
    } catch (e) {
      toast.error("Failed to load trash");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const total =
    (trash.applications?.length || 0) +
    (trash.domestic?.length || 0) +
    (trash.recruiters?.length || 0) +
    (trash["student-aid"]?.length || 0);

  if (loading)
    return <div className="p-10 text-slate-500 text-sm">Loading...</div>;

  return (
    <div className="space-y-5">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 to-slate-700 text-white p-6"
      >
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-amber-500/15 blur-3xl" />
        <div className="relative flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center border border-white/10">
            <Trash2 className="w-6 h-6 text-amber-300" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-amber-300 font-semibold">
              Recycle Bin
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-semibold">
              {total} item{total !== 1 ? "s" : ""} in trash
            </h1>
            <p className="text-white/70 text-sm mt-0.5">
              Restore items back to active lists, or delete them forever.
            </p>
          </div>
        </div>
      </motion.div>

      <Section
        kind="applications"
        items={trash.applications || []}
        refresh={load}
      />
      <Section
        kind="domestic"
        items={trash.domestic || []}
        refresh={load}
      />
      <Section
        kind="recruiters"
        items={trash.recruiters || []}
        refresh={load}
      />
      <Section
        kind="student-aid"
        items={trash["student-aid"] || []}
        refresh={load}
      />
    </div>
  );
}
